import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CompanyContext } from '@models/companies';
import { DataContext } from '@services/data-context';
import { MessageService } from '@services/messages';
import { CompanyAccessService } from './company-access.service';

export const hasCompanyAccessGuard: CanActivateFn = (route) => {
  const dataContext = inject(DataContext);
  const companyAccess = inject(CompanyAccessService);
  const router = inject(Router);
  const message = inject(MessageService);
  const translate = inject(TranslateService);

  const projectId = route.paramMap.get('projectId') ?? '';
  const companyId = route.paramMap.get('companyId') ?? '';

  const projectContext = dataContext.matchProjectContext(projectId);
  const companyContext = dataContext.matchCompanyContext(companyId);

  if (projectContext && companyContext) {
    if (isMemberOfRouteCompany(companyContext)) {
      void resolveAccessAsync();
      return true;
    }

    denyAccess();
    return false;
  }

  return resolveAccessAsync();

  async function resolveAccessAsync(): Promise<boolean> {
    await companyAccess.loadProjectDetailsAsync(projectId);
    const companyLoaded = await companyAccess.loadCompanyByIdAsync(companyId);

    if (!dataContext.isRequestedProject(projectId) || !dataContext.isRequestedCompany(companyId)) {
      return false;
    }

    const context = dataContext.companyContext();
    if (!companyLoaded || !context || !isMemberOfRouteCompany(context)) {
      denyAccess();
      return false;
    }

    dataContext.setClientAccess(projectId, true);
    return true;
  }

  function isMemberOfRouteCompany(context: CompanyContext): boolean {
    // The company must belong to the project in the URL, otherwise a company id could be paired with a foreign project.
    const belongsToProject = context.company()?.ProjectId === projectId;
    return belongsToProject && (context.isCompanyLeader() || context.isCompanyUser());
  }

  function denyAccess(): void {
    dataContext.clearCompanyContext();
    message.showErrorMessage(translate.instant('general.companyAccessRequired'));
    void router.navigate(['/projects']);
  }
};
